<!--
  Calibration — the volumetric calibration worksheet plus the 10 pre-pour
  calibration items.

  Spec, Screens 6: "Calibration — three trials side by side with the verdict
  live."

  Trials and checks are separate, because they are separate activities: three
  trials CALIBRATE and carry no verdict; two checks VERIFY. The worksheet lays
  them out that way (columns B-D and E-H) and this screen follows it. Putting
  the check on the trial rows implied three checks and produced a verdict cell
  that could never be filled.

  What is computed here (all of it imported from ../../lib/calc, none of it
  reimplemented):
    Wt/count per trial     reading_kg / counts                    (Appendix C step 7)
    Actual wt/count        ROUND(actual_kg / actual_counts, 4)    (per check)
    Variance vs check      vs the entered check wt/count, judged
                           at the SITE +/-2% — NOT CSA A23.1      (Appendix C step 6)
    Water check            water_kg / water_litres at +/-1%       (Appendix C step 8)

  The check's +/-2% is wider than CSA A23.1's +/-1% on purpose and must never be
  labelled CSA anywhere a person can read it — see CHECK_TOLERANCE_FRACTION in
  ../../lib/types and the spec at
  docs/superpowers/specs/2026-07-29-calibration-checks-design.md.

  Why `trial_wt_per_count_used` is NEVER auto-filled from the trials
  ------------------------------------------------------------------
  Appendix C step 5 says to photograph the batch computer screen for counts/kg;
  step 6 then multiplies that figure by the actual counts and compares it with
  the scale weight. The whole point of the CSA A23.1 +/-1% check is to prove the
  BATCH COMPUTER predicts the scale. `trial_wt_per_count_used` is the batch
  computer's figure, so it is typed in from the screen (and evidenced by the
  photo next to it). Adopting the mean of `reading_kg / counts` — which is
  derived from the scale readings themselves — would turn the check into
  scale-vs-scale and produce a near-guaranteed pass that proves nothing. The
  trial mean is therefore shown as the step 7 BACK-CHECK only, with no control
  that copies it into the adopted value.

  The three trials are a genuine side-by-side grid — a row per measurement, a
  column per trial — because that is how the paper worksheet reads and how a QC
  person compares trials. It scrolls horizontally inside its own container on a
  narrow phone rather than reflowing into three stacked blocks that cannot be
  compared.

  Note on the two temperature groups: the pre-pour items carry ambient / water /
  grout temperatures for the pour, and the worksheet separately carries the
  material and ambient temperatures at the moment of the calibration test.
  Both are in the data model; both are rendered, under headings that say which
  is which.
-->
<script lang="ts">
  import type {
    CalibrationCheck,
    CalibrationCheckPatch,
    CalibrationTrial,
    CalibrationTrialPatch,
    Photo,
    PrePourCalibration,
    PrePourCalibrationPatch,
    SqlBool,
    Timestamp
  } from '../../lib/types';
  import {
    CALIBRATION_CHECK_COUNT,
    CALIBRATION_TRIAL_COUNT,
    CHECK_TOLERANCE_FRACTION,
    CHECK_WT_PER_COUNT_DECIMALS,
    TOLERANCE_FRACTION
  } from '../../lib/types';
  import { calibrationComputed, waterCheck, wtPerCount } from '../../lib/calc';
  import NumberField from '../../lib/fields/NumberField.svelte';
  import YesNoField from '../../lib/fields/YesNoField.svelte';
  import NotesField from '../../lib/fields/NotesField.svelte';
  import PhotoField from '../../lib/fields/PhotoField.svelte';
  import VerdictBadge from '../../lib/fields/VerdictBadge.svelte';
  import { clearDraft, loadDraft, saveDraft } from '../../lib/fields/local-draft';

  interface Props {
    pourId: number;
    calibration: PrePourCalibration;
    /** Always CALIBRATION_TRIAL_COUNT rows; the API seeds them with the pour. */
    trials: CalibrationTrial[];
    /** Always CALIBRATION_CHECK_COUNT rows; the API seeds them with the pour. */
    checks: CalibrationCheck[];
    /**
     * The whole pour's photos. Each trial's PhotoField picks out its own
     * (section 'calibration_trial', row_id = that trial's id) — Appendix C
     * step 5, "photograph the batch computer screen".
     */
    photos?: Photo[];
    readonly?: boolean;
    /**
     * Photos alone stay editable after the pour closes — the owner wants
     * evidence addable after close. Deliberately REQUIRED, not defaulted to
     * `readonly`: a caller passing neither `readonly` nor `photosReadOnly`
     * would otherwise get an explicit `readOnly={false}` on the nested
     * PhotoField, which overrides PhotoField's own role-derived safety
     * default (`readOnly = currentRole() !== 'crew'`) — a non-crew session
     * gaining a photo control it should not have. Making this required turns
     * that failure mode into a type error instead.
     */
    photosReadOnly: boolean;
    /** The pour's `closed_at`, threaded through to the trial PhotoFields. */
    closedAt?: Timestamp | null;
    /**
     * Section-blur flush. Maps straight onto the `calibration` and
     * `calibration_trials` members of UpdatePourRequest, so the whole section
     * lands in one transaction and one audit entry per sub-section.
     */
    onSave?: (patch: {
      calibration?: PrePourCalibrationPatch;
      calibration_trials?: CalibrationTrialPatch[];
      calibration_checks?: CalibrationCheckPatch[];
    }) => Promise<void>;
  }

  let {
    pourId,
    calibration,
    trials,
    checks,
    photos = [],
    readonly = false,
    photosReadOnly,
    closedAt = null,
    onSave
  }: Props = $props();

  /* ------------------------------------------------------------------ draft */

  const DRAFT_KEY = `calibration.${pourId}`;

  interface DraftState {
    cal: PrePourCalibrationPatch;
    /** keyed by trial_no (1..3), which is what CalibrationTrialPatch identifies by */
    trial: Record<number, Omit<CalibrationTrialPatch, 'trial_no'>>;
    /** keyed by check_no (1..2), which is what CalibrationCheckPatch identifies by */
    check: Record<number, Omit<CalibrationCheckPatch, 'check_no'>>;
    calDirty: string[];
    trialDirty: Record<number, string[]>;
    checkDirty: Record<number, string[]>;
  }

  const restored = loadDraft<DraftState>(DRAFT_KEY);
  let cal = $state<PrePourCalibrationPatch>(restored?.cal ?? {});
  let trial = $state<Record<number, Omit<CalibrationTrialPatch, 'trial_no'>>>(
    restored?.trial ?? {}
  );
  let check = $state<Record<number, Omit<CalibrationCheckPatch, 'check_no'>>>(
    restored?.check ?? {}
  );
  let calDirty = $state<string[]>(restored?.calDirty ?? []);
  let trialDirty = $state<Record<number, string[]>>(restored?.trialDirty ?? {});
  let checkDirty = $state<Record<number, string[]>>(restored?.checkDirty ?? {});

  let saving = $state(false);
  let error = $state<string | null>(null);
  let showProcedure = $state(false);

  $effect(() => {
    const snapshot: DraftState = {
      cal: $state.snapshot(cal),
      trial: $state.snapshot(trial),
      check: $state.snapshot(check),
      calDirty: $state.snapshot(calDirty),
      trialDirty: $state.snapshot(trialDirty),
      checkDirty: $state.snapshot(checkDirty)
    };
    const empty =
      Object.keys(snapshot.cal).length === 0 &&
      Object.keys(snapshot.trial).length === 0 &&
      Object.keys(snapshot.check).length === 0;
    if (empty) clearDraft(DRAFT_KEY);
    else saveDraft(DRAFT_KEY, snapshot);
  });

  /** Server row with the local overlay on top — what the screen renders. */
  let view = $derived({ ...calibration, ...cal } as PrePourCalibration);

  let trialRows = $derived(
    Array.from({ length: CALIBRATION_TRIAL_COUNT }, (_, i) => i + 1).map((no) => {
      const base =
        trials.find((t) => t.trial_no === no) ??
        ({
          id: -no,
          pour_id: pourId,
          trial_no: no,
          reading_kg: null,
          counts: null,
          created_at: '',
          updated_at: ''
        } as CalibrationTrial);
      return { ...base, ...(trial[no] ?? {}) } as CalibrationTrial;
    })
  );

  let checkRows = $derived(
    Array.from({ length: CALIBRATION_CHECK_COUNT }, (_, i) => i + 1).map((no) => {
      const base =
        checks.find((x) => x.check_no === no) ??
        ({
          id: -no,
          pour_id: pourId,
          check_no: no,
          check_counts: null,
          check_kg: null,
          check_wt_per_count: null,
          actual_counts: null,
          actual_kg: null,
          created_at: '',
          updated_at: ''
        } as CalibrationCheck);
      return { ...base, ...(check[no] ?? {}) } as CalibrationCheck;
    })
  );

  let dirty = $derived(
    calDirty.length > 0 ||
      Object.values(trialDirty).some((keys) => keys.length > 0) ||
      Object.values(checkDirty).some((keys) => keys.length > 0)
  );

  function setCal<K extends keyof PrePourCalibrationPatch>(
    key: K,
    v: PrePourCalibrationPatch[K]
  ) {
    if (readonly) return;
    cal[key] = v;
    if (!calDirty.includes(key as string)) calDirty = [...calDirty, key as string];
  }

  type TrialField = 'reading_kg' | 'counts';

  function setTrial(no: number, key: TrialField, v: number | null) {
    if (readonly) return;
    if (!trial[no]) trial[no] = {};
    trial[no][key] = v;
    const keys = trialDirty[no] ?? [];
    if (!keys.includes(key)) trialDirty[no] = [...keys, key];
  }

  type CheckField =
    | 'check_counts'
    | 'check_kg'
    | 'check_wt_per_count'
    | 'actual_counts'
    | 'actual_kg';

  function setCheck(no: number, key: CheckField, v: number | null) {
    if (readonly) return;
    if (!check[no]) check[no] = {};
    check[no][key] = v;
    const keys = checkDirty[no] ?? [];
    if (!keys.includes(key)) checkDirty[no] = [...keys, key];
  }

  /**
   * Flush the whole section in one PATCH.
   *
   * On success the overlay is pruned only where the server row now agrees, so
   * if the parent has not yet refreshed the prop the screen keeps showing what
   * was typed. On failure nothing is discarded — the draft stays on the device
   * with an unsaved state and a retry, per the spec's error handling.
   */
  async function flush() {
    if (readonly || saving || !dirty) return;

    const calPatch: Record<string, unknown> = {};
    for (const key of calDirty) calPatch[key] = (cal as Record<string, unknown>)[key];

    const trialPatches: CalibrationTrialPatch[] = [];
    for (const [noStr, keys] of Object.entries(trialDirty)) {
      if (keys.length === 0) continue;
      const no = Number(noStr);
      const patch: CalibrationTrialPatch = { trial_no: no };
      for (const key of keys) {
        (patch as unknown as Record<string, unknown>)[key] = (
          trial[no] as Record<string, unknown> | undefined
        )?.[key];
      }
      trialPatches.push(patch);
    }

    const checkPatches: CalibrationCheckPatch[] = [];
    for (const [noStr, keys] of Object.entries(checkDirty)) {
      if (keys.length === 0) continue;
      const no = Number(noStr);
      const patch: CalibrationCheckPatch = { check_no: no };
      for (const key of keys) {
        (patch as unknown as Record<string, unknown>)[key] = (
          check[no] as Record<string, unknown> | undefined
        )?.[key];
      }
      checkPatches.push(patch);
    }

    const sentCalKeys = [...calDirty];
    const sentTrialKeys: Record<number, string[]> = {};
    for (const [noStr, keys] of Object.entries(trialDirty)) sentTrialKeys[Number(noStr)] = [...keys];
    const sentCheckKeys: Record<number, string[]> = {};
    for (const [noStr, keys] of Object.entries(checkDirty)) sentCheckKeys[Number(noStr)] = [...keys];

    saving = true;
    error = null;
    try {
      await onSave?.({
        calibration: sentCalKeys.length > 0 ? (calPatch as PrePourCalibrationPatch) : undefined,
        calibration_trials: trialPatches.length > 0 ? trialPatches : undefined,
        calibration_checks: checkPatches.length > 0 ? checkPatches : undefined
      });

      calDirty = calDirty.filter(
        (k) => !sentCalKeys.includes(k) || (cal as Record<string, unknown>)[k] !== calPatch[k]
      );
      for (const [noStr, keys] of Object.entries(sentTrialKeys)) {
        const no = Number(noStr);
        const sentPatch = trialPatches.find((p) => p.trial_no === no) ?? {};
        trialDirty[no] = (trialDirty[no] ?? []).filter(
          (k) =>
            !keys.includes(k) ||
            (trial[no] as Record<string, unknown> | undefined)?.[k] !==
              (sentPatch as Record<string, unknown>)[k]
        );
        if (trialDirty[no].length === 0) delete trialDirty[no];
      }
      for (const [noStr, keys] of Object.entries(sentCheckKeys)) {
        const no = Number(noStr);
        const sentPatch = checkPatches.find((p) => p.check_no === no) ?? {};
        checkDirty[no] = (checkDirty[no] ?? []).filter(
          (k) =>
            !keys.includes(k) ||
            (check[no] as Record<string, unknown> | undefined)?.[k] !==
              (sentPatch as Record<string, unknown>)[k]
        );
        if (checkDirty[no].length === 0) delete checkDirty[no];
      }
      prune();
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not save. Tap retry.';
    } finally {
      saving = false;
    }
  }

  /** Drop overlay values the server row now agrees with. */
  function prune() {
    for (const k of Object.keys(cal) as (keyof PrePourCalibrationPatch)[]) {
      if (calDirty.includes(k as string)) continue;
      if ((calibration as unknown as Record<string, unknown>)[k as string] === cal[k]) delete cal[k];
    }
    for (const noStr of Object.keys(trial)) {
      const no = Number(noStr);
      const base = trials.find((t) => t.trial_no === no);
      const draft = trial[no];
      if (!base || !draft) continue;
      for (const k of Object.keys(draft) as TrialField[]) {
        if (trialDirty[no]?.includes(k)) continue;
        if ((base as unknown as Record<string, unknown>)[k] === draft[k]) delete draft[k];
      }
      if (Object.keys(draft).length === 0) delete trial[no];
    }
    for (const noStr of Object.keys(check)) {
      const no = Number(noStr);
      const base = checks.find((x) => x.check_no === no);
      const draft = check[no];
      if (!base || !draft) continue;
      for (const k of Object.keys(draft) as CheckField[]) {
        if (checkDirty[no]?.includes(k)) continue;
        if ((base as unknown as Record<string, unknown>)[k] === draft[k]) delete draft[k];
      }
      if (Object.keys(draft).length === 0) delete check[no];
    }
  }

  /* -------------------------------------------------------------- computed */

  let computed = $derived(
    calibrationComputed(
      {
        trial_wt_per_count_used: view.trial_wt_per_count_used,
        trial_water_litres: view.trial_water_litres,
        trial_water_kg: view.trial_water_kg
      },
      trialRows.map((t) => ({
        trial_no: t.trial_no,
        reading_kg: t.reading_kg,
        counts: t.counts
      })),
      checkRows.map((x) => ({
        check_no: x.check_no,
        check_counts: x.check_counts,
        check_kg: x.check_kg,
        check_wt_per_count: x.check_wt_per_count,
        actual_counts: x.actual_counts,
        actual_kg: x.actual_kg
      }))
    )
  );

  function checkComputedFor(no: number) {
    return computed.checks.find((x) => x.check_no === no) ?? null;
  }

  /**
   * Same check as `computed.water_check`, taken straight from calc because the
   * worksheet prints the litres/kg RATIO next to the verdict and the shared
   * `ToleranceCheck` wire shape does not carry it. Identical inputs, identical
   * function — no second implementation of the rule.
   */
  let waterResult = $derived(waterCheck(view.trial_water_litres, view.trial_water_kg));

  const TOLERANCE_LABEL = `±${(TOLERANCE_FRACTION * 100).toFixed(0)}%`;
  /** Site tolerance for the calibration check. Deliberately not CSA-labelled. */
  const CHECK_TOLERANCE_LABEL = `±${(CHECK_TOLERANCE_FRACTION * 100).toFixed(0)}%`;

  function fmt(v: number | null | undefined, dp: number): string {
    return v === null || v === undefined ? '—' : v.toFixed(dp);
  }

  function timeValue(v: string | null): string {
    if (!v) return '';
    if (/^\d{2}:\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
</script>

<section class="cal" aria-label="Pre-pour calibration">
  <header class="head">
    <h2>Pre-pour calibration</h2>
    <div class="state" aria-live="polite">
      {#if saving}
        <span class="pill saving">Saving…</span>
      {:else if dirty}
        <span class="pill dirty">Unsaved</span>
        <button type="button" class="retry" onclick={flush}>Save</button>
      {/if}
    </div>
  </header>

  {#if error}
    <p class="banner bad" role="alert">
      {error}
      <button type="button" class="retry" onclick={flush}>Retry</button>
    </p>
  {/if}

  <!-- ============================================ the 10 pre-pour items -->
  <div class="block">
    <h3>Settings</h3>
    <div class="grid">
      <NumberField
        label="Approved water dosage"
        value={view.approved_water_dosage}
        disabled={readonly}
        compact
        onValue={(v) => setCal('approved_water_dosage', v)}
        onCommit={flush}
      />
      <NumberField
        label="Dry feed setting"
        value={view.dry_feed_setting}
        disabled={readonly}
        compact
        onValue={(v) => setCal('dry_feed_setting', v)}
        onCommit={flush}
      />
      <NumberField
        label="Water flow meter setting"
        value={view.water_flow_meter_setting}
        disabled={readonly}
        compact
        onValue={(v) => setCal('water_flow_meter_setting', v)}
        onCommit={flush}
      />
      <NumberField
        label="Approved production rate"
        unit="m³/h"
        value={view.approved_production_rate}
        disabled={readonly}
        compact
        onValue={(v) => setCal('approved_production_rate', v)}
        onCommit={flush}
      />
    </div>

    <div class="grid">
      <YesNoField
        label="Material feed stable"
        value={view.material_feed_stable as SqlBool | null}
        disabled={readonly}
        compact
        onValue={(v) => {
          setCal('material_feed_stable', v as SqlBool | null);
          flush();
        }}
      />
      <YesNoField
        label="Water input stable"
        value={view.water_input_stable as SqlBool | null}
        disabled={readonly}
        compact
        onValue={(v) => {
          setCal('water_input_stable', v as SqlBool | null);
          flush();
        }}
      />
    </div>

    <h3>Pour temperatures</h3>
    <div class="grid three">
      <NumberField
        label="Ambient"
        unit="°C"
        value={view.temp_ambient}
        disabled={readonly}
        compact
        onValue={(v) => setCal('temp_ambient', v)}
        onCommit={flush}
      />
      <NumberField
        label="Water"
        unit="°C"
        value={view.temp_water}
        disabled={readonly}
        compact
        onValue={(v) => setCal('temp_water', v)}
        onCommit={flush}
      />
      <NumberField
        label="Grout"
        unit="°C"
        value={view.temp_grout}
        disabled={readonly}
        compact
        onValue={(v) => setCal('temp_grout', v)}
        onCommit={flush}
      />
    </div>

    <NotesField
      label="Pump / line readiness"
      value={view.pump_line_readiness}
      rows={2}
      disabled={readonly}
      onValue={(v) => setCal('pump_line_readiness', v)}
      onCommit={flush}
    />
    <NotesField
      label="Acceptance point"
      value={view.acceptance_point}
      rows={2}
      disabled={readonly}
      onValue={(v) => setCal('acceptance_point', v)}
      onCommit={flush}
    />
    <NotesField
      label="Stop / hold contacts"
      value={view.stop_hold_contacts}
      rows={2}
      disabled={readonly}
      onValue={(v) => setCal('stop_hold_contacts', v)}
      onCommit={flush}
    />
  </div>

  <!-- ================================================ calibration trials -->
  <div class="block">
    <h3>Calibration trials</h3>
    <p class="note">
      Records of what came off the scale and what the computer counted (steps 4-5, back-check
      step 7). A trial carries no verdict — the verification is the two checks below.
    </p>

    <div class="scroller">
      <div class="trials" style="--trials: {CALIBRATION_TRIAL_COUNT}">
        <div class="rowlabel corner"></div>
        {#each trialRows as t (t.trial_no)}
          <div class="colhead">Trial {t.trial_no}</div>
        {/each}

        <div class="rowlabel">Reading (kg)</div>
        {#each trialRows as t (t.trial_no)}
          <div class="cell">
            <NumberField
              label="Trial {t.trial_no} reading in kg"
              hideLabel
              compact
              unit="kg"
              value={t.reading_kg}
              disabled={readonly}
              onValue={(v) => setTrial(t.trial_no, 'reading_kg', v)}
              onCommit={flush}
            />
          </div>
        {/each}

        <div class="rowlabel">Counts</div>
        {#each trialRows as t (t.trial_no)}
          <div class="cell">
            <NumberField
              label="Trial {t.trial_no} counts"
              hideLabel
              compact
              integer
              value={t.counts}
              disabled={readonly}
              onValue={(v) => setTrial(t.trial_no, 'counts', v)}
              onCommit={flush}
            />
          </div>
        {/each}

        <div class="rowlabel computed">Wt/count</div>
        {#each trialRows as t (t.trial_no)}
          <div class="cell computed" aria-live="polite">
            <output>{fmt(wtPerCount(t.reading_kg, t.counts), 4)}</output>
          </div>
        {/each}

      </div>
    </div>

    <div class="adopted">
      <NumberField
        label="Counts/kg from the batch computer"
        unit="kg/count"
        value={view.trial_wt_per_count_used}
        hint="Read off the batch computer screen (step 5) and photographed below. Type it in — it is never copied from the trial readings, because the ±1% check exists to prove the computer predicts the scale."
        disabled={readonly}
        onValue={(v) => setCal('trial_wt_per_count_used', v)}
        onCommit={flush}
      />
      <div class="mean">
        <VerdictBadge
          tone={computed.wt_per_count_mean === null ? 'muted' : 'info'}
          label="Trial mean (back-check)"
          detail={fmt(computed.wt_per_count_mean, 4)}
        />
        <span class="sub">
          Scale-derived (step 7). Compare it by eye with the computer figure; it is
          deliberately not adopted as the value the check is judged against.
        </span>
      </div>
    </div>
  </div>

  <!-- ================================================ calibration checks -->
  <div class="block">
    <h3>Calibration checks</h3>
    <p class="note">
      The verification (step 6). Enter the batch computer's figures on the Check row and what
      the scale actually gave on the Actual rows. Every Check figure is typed in — none of it
      is copied from the trials, because the check exists to prove the computer predicts the
      scale.
    </p>

    <div class="scroller">
      <div class="trials" style="--trials: {CALIBRATION_CHECK_COUNT}">
        <div class="rowlabel corner"></div>
        {#each checkRows as x (x.check_no)}
          <div class="colhead">Check {x.check_no}</div>
        {/each}

        <div class="rowlabel">Check counts</div>
        {#each checkRows as x (x.check_no)}
          <div class="cell">
            <NumberField
              label="Check {x.check_no} counts"
              hideLabel
              compact
              integer
              value={x.check_counts}
              disabled={readonly}
              onValue={(v) => setCheck(x.check_no, 'check_counts', v)}
              onCommit={flush}
            />
          </div>
        {/each}

        <div class="rowlabel">Check weight (kg)</div>
        {#each checkRows as x (x.check_no)}
          <div class="cell">
            <NumberField
              label="Check {x.check_no} weight in kg"
              hideLabel
              compact
              unit="kg"
              value={x.check_kg}
              disabled={readonly}
              onValue={(v) => setCheck(x.check_no, 'check_kg', v)}
              onCommit={flush}
            />
          </div>
        {/each}

        <div class="rowlabel">Check wt/count</div>
        {#each checkRows as x (x.check_no)}
          <div class="cell">
            <NumberField
              label="Check {x.check_no} wt per count"
              hideLabel
              compact
              unit="kg/count"
              value={x.check_wt_per_count}
              disabled={readonly}
              onValue={(v) => setCheck(x.check_no, 'check_wt_per_count', v)}
              onCommit={flush}
            />
          </div>
        {/each}

        <div class="rowlabel">Actual counts</div>
        {#each checkRows as x (x.check_no)}
          <div class="cell">
            <NumberField
              label="Check {x.check_no} actual counts"
              hideLabel
              compact
              integer
              value={x.actual_counts}
              disabled={readonly}
              onValue={(v) => setCheck(x.check_no, 'actual_counts', v)}
              onCommit={flush}
            />
          </div>
        {/each}

        <div class="rowlabel">Actual (kg)</div>
        {#each checkRows as x (x.check_no)}
          <div class="cell">
            <NumberField
              label="Check {x.check_no} actual weight in kg"
              hideLabel
              compact
              unit="kg"
              value={x.actual_kg}
              disabled={readonly}
              onValue={(v) => setCheck(x.check_no, 'actual_kg', v)}
              onCommit={flush}
            />
          </div>
        {/each}

        <div class="rowlabel computed">Actual wt/count</div>
        {#each checkRows as x (x.check_no)}
          <div class="cell computed" aria-live="polite">
            <output>
              {fmt(checkComputedFor(x.check_no)?.actual_wt_per_count, CHECK_WT_PER_COUNT_DECIMALS)}
            </output>
          </div>
        {/each}

        <!-- SITE tolerance, not CSA A23.1. See CHECK_TOLERANCE_FRACTION. -->
        <div class="rowlabel computed">
          Variance vs check<br /><small>Site {CHECK_TOLERANCE_LABEL}</small>
        </div>
        {#each checkRows as x (x.check_no)}
          {@const variance = checkComputedFor(x.check_no)?.variance ?? null}
          <div class="cell computed" aria-live="polite">
            {#if variance === null}
              <VerdictBadge compact tone="muted" label="—" detail="needs check wt/count" />
            {:else}
              <VerdictBadge
                compact
                tone={variance.verdict === 'pass' ? 'pass' : 'fail'}
                label={variance.verdict === 'pass' ? 'Pass' : 'Fail'}
                detail="{variance.deviation >= 0 ? '+' : '−'}{variance.deviation_percent.toFixed(
                  2
                )}%"
              />
            {/if}
          </div>
        {/each}
      </div>
    </div>

    <p class="note">
      Actual wt/count is <code>actual kg ÷ actual counts</code>, rounded to
      {CHECK_WT_PER_COUNT_DECIMALS} decimals, and the variance is measured against the Check
      wt/count. The worksheet rounds to 3; this keeps a decimal more so the percentage can tell
      two readings apart — at 3 decimals the figure moves in steps of about 1.3% and the
      variance barely changed. The ±2% is a site tolerance, <strong>not</strong> CSA A23.1
      (±1%).
    </p>
  </div>

  <!-- ============================== batch computer photos, per trial (C.5) -->
  <div class="block">
    <h3>Batch computer photos</h3>
    <p class="note">
      Appendix C step 5: photograph the batch computer screen for counts/kg and the number of
      counts. Each photo attaches to the trial it evidences.
    </p>
    <div class="grid">
      {#each trialRows as t (t.trial_no)}
        <PhotoField
          {pourId}
          section="calibration_trial"
          rowId={t.id > 0 ? t.id : null}
          {photos}
          label="Trial {t.trial_no}"
          actionLabel="📷 Trial {t.trial_no} screen"
          caption="Batch computer — trial {t.trial_no}"
          readOnly={photosReadOnly}
          {closedAt}
        />
      {/each}
    </div>
  </div>

  <!-- ======================================================= water check -->
  <div class="block">
    <h3>Water check <small>litres dispensed = weight in kg, {TOLERANCE_LABEL}</small></h3>
    <div class="grid three">
      <NumberField
        label="Water dispensed"
        unit="L"
        value={view.trial_water_litres}
        disabled={readonly}
        compact
        onValue={(v) => setCal('trial_water_litres', v)}
        onCommit={flush}
      />
      <NumberField
        label="Water weight"
        unit="kg"
        value={view.trial_water_kg}
        disabled={readonly}
        compact
        onValue={(v) => setCal('trial_water_kg', v)}
        onCommit={flush}
      />
      <div class="verdict" aria-live="polite">
        {#if waterResult === null}
          <VerdictBadge tone="muted" label="Water" detail="—" />
        {:else}
          <VerdictBadge
            tone={waterResult.verdict === 'pass' ? 'pass' : 'fail'}
            label={waterResult.verdict === 'pass' ? 'Pass' : 'Fail'}
            detail="L/kg {waterResult.ratio.toFixed(4)}"
          />
          <span class="sub">
            {waterResult.deviation >= 0 ? '+' : '−'}{waterResult.deviation_percent.toFixed(2)}%
          </span>
        {/if}
      </div>
    </div>
  </div>

  <!-- ============================================== worksheet conditions -->
  <div class="block">
    <h3>Test conditions</h3>
    <div class="grid">
      <label class="text">
        <span>Test start</span>
        <input
          type="time"
          disabled={readonly}
          value={timeValue(view.trial_test_start)}
          oninput={(e) =>
            setCal('trial_test_start', e.currentTarget.value === '' ? null : e.currentTarget.value)}
          onblur={flush}
        />
      </label>
      <label class="text">
        <span>Test end</span>
        <input
          type="time"
          disabled={readonly}
          value={timeValue(view.trial_test_end)}
          oninput={(e) =>
            setCal('trial_test_end', e.currentTarget.value === '' ? null : e.currentTarget.value)}
          onblur={flush}
        />
      </label>
      <NumberField
        label="Material temp"
        unit="°C"
        value={view.trial_temp_material}
        disabled={readonly}
        compact
        onValue={(v) => setCal('trial_temp_material', v)}
        onCommit={flush}
      />
      <NumberField
        label="Ambient temp (test)"
        unit="°C"
        value={view.trial_temp_ambient}
        disabled={readonly}
        compact
        onValue={(v) => setCal('trial_temp_ambient', v)}
        onCommit={flush}
      />
    </div>
  </div>

  <!-- Appendix C, kept to hand because the paper worksheet prints it. -->
  <div class="block procedure">
    <button
      type="button"
      class="disclose"
      aria-expanded={showProcedure}
      onclick={() => (showProcedure = !showProcedure)}
    >
      <span aria-hidden="true">{showProcedure ? '▾' : '▸'}</span> Calibration procedure
    </button>
    {#if showProcedure}
      <ol class="steps">
        <li>Start the volumetric mixer and prime grout into the auger; dispose of priming grout.</li>
        <li>Put an empty bucket on the scale and zero it.</li>
        <li>Discharge 50–65 kg into the bucket.</li>
        <li>Obtain weight and record the result.</li>
        <li>Photograph the batch computer screen for counts/kg and number of counts.</li>
        <li>Calculate counts/kg × actual counts.</li>
        <li>Back-check: actual weight ÷ actual counts.</li>
        <li>Check water: litres dispensed = weight in kg, expect {TOLERANCE_LABEL}.</li>
        <li>Check spread: target 16–19 in. Below 16, add water; above 19, reduce water dosage rate.</li>
        <li>Validate with flow: target 40–70 s. Below 40, too much water; above 70, not enough.</li>
      </ol>
    {/if}
  </div>
</section>

<style>
  .cal {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  h2 {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 800;
  }

  h3 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--fg-muted, #3f4750);
  }

  h3 small {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
  }

  .state {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-left: auto;
  }

  .pill {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
  }

  .pill.dirty {
    background: var(--warn-bg, #fff6e6);
    color: var(--warn, #8a4b00);
  }

  .pill.saving {
    background: var(--surface-muted, #eef1f4);
    color: var(--fg-muted, #3f4750);
  }

  .banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .banner.bad {
    background: var(--bad-bg, #fdecea);
    color: var(--bad, #b3261e);
  }

  .retry {
    min-height: 2.5rem;
    padding: 0 0.75rem;
    font: inherit;
    font-weight: 800;
    border-radius: 0.4rem;
    background: transparent;
    cursor: pointer;
    touch-action: manipulation;
    border: 2px solid currentColor;
    color: inherit;
  }

  .block {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 0.75rem;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.625rem;
    background: var(--surface, #fff);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: 0.5rem;
  }

  .grid.three {
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
  }

  /* --- the side-by-side trial grid --------------------------------------- */

  .scroller {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -0.25rem;
    padding: 0 0.25rem;
  }

  .trials {
    display: grid;
    grid-template-columns: minmax(6rem, auto) repeat(var(--trials), minmax(7rem, 1fr));
    gap: 0.375rem;
    align-items: center;
    min-width: 28rem;
  }

  .colhead {
    font-size: 0.8125rem;
    font-weight: 800;
    text-align: center;
    padding: 0.25rem 0;
    background: var(--surface-muted, #eef1f4);
    border-radius: 0.375rem;
  }

  .corner {
    background: transparent;
  }

  .rowlabel {
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1.2;
    color: var(--fg-muted, #3f4750);
  }

  .rowlabel small {
    font-weight: 600;
    font-size: 0.6875rem;
  }

  .rowlabel.computed,
  .cell.computed {
    background: var(--surface-muted, #eef1f4);
    border-radius: 0.375rem;
    padding: 0.375rem;
  }

  .cell {
    min-width: 0;
  }

  .cell.computed {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    text-align: center;
  }

  .cell.computed output {
    font-size: 1rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: var(--fg, #10151b);
  }

  .sub {
    font-size: 0.6875rem;
    font-variant-numeric: tabular-nums;
    color: var(--fg-muted, #3f4750);
  }

  .adopted {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
    gap: 0.5rem;
    align-items: end;
  }

  .mean {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .note {
    margin: 0;
    font-size: 0.75rem;
    line-height: 1.3;
    color: var(--fg-muted, #3f4750);
  }

  .verdict {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 0.2rem;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .text span {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--fg-muted, #3f4750);
  }

  .text input {
    box-sizing: border-box;
    width: 100%;
    /* 16px minimum so iOS Safari does not zoom on focus. */
    font-size: 1rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    min-height: 3rem;
    padding: 0.5rem;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--fg, #10151b);
  }

  .text input:focus {
    outline: none;
    border-color: var(--accent, #0b63c5);
    box-shadow: 0 0 0 3px var(--accent-ring, rgba(11, 99, 197, 0.28));
  }

  .procedure {
    background: var(--surface-muted, #eef1f4);
  }

  .disclose {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 3rem;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-weight: 800;
    color: var(--fg, #10151b);
    cursor: pointer;
    touch-action: manipulation;
  }

  .steps {
    margin: 0;
    padding-left: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    font-size: 0.875rem;
    line-height: 1.35;
    color: var(--fg, #10151b);
  }
</style>
